import { Link } from 'react-router';
import { BarList, StatTile, TrendChart } from '../../components/charts.jsx';
import { ErrorState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { WelcomeBanner } from '../../components/WelcomeBanner.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatXaf, titleCase } from '../../lib/format.js';

const AdminOverview = () => {
  useTitle('Platform overview');
  const { data, loading, error, reload } = useFetch('/admin/analytics');

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { totals } = data;

  const statusRows = (record, order) =>
    order
      .filter((key) => record[key] !== undefined)
      .map((key) => ({ key, label: titleCase(key), value: record[key] }));

  return (
    <div className="container section">
      <WelcomeBanner
        eyebrow="Admin"
        title="Platform overview"
        text="Everything on the platform at a glance. Trends cover the last 30 days."
      >
        {totals.pendingListings > 0 ? (
          <Link to="/admin/listings" className="btn btn-primary">
            Review {totals.pendingListings} listing{totals.pendingListings === 1 ? '' : 's'}
          </Link>
        ) : null}
        {totals.openReports > 0 ? (
          <Link to="/admin/reports" className="btn btn-outline">
            {totals.openReports} open report{totals.openReports === 1 ? '' : 's'}
          </Link>
        ) : null}
      </WelcomeBanner>

      <div className="stat-row">
        <StatTile value={totals.liveListings} label="Live listings" note={`${totals.listings} created in total`} />
        <StatTile value={totals.users} label="Registered users" note={`${totals.landlords} landlords, ${totals.tenants} tenants`} />
        <StatTile value={totals.applications} label="Applications submitted" />
        <StatTile value={totals.views30d} label="Listing views, 30 days" />
      </div>

      <div className="stat-row">
        <StatTile
          value={data.medianRent ? formatXaf(data.medianRent) : '-'}
          label="Median advertised rent"
          note="Across approved listings"
        />
        <StatTile value={totals.messages} label="Messages exchanged" />
        <StatTile
          value={totals.pendingListings}
          label="Listings awaiting review"
          tone={totals.pendingListings > 0 ? 'warn' : undefined}
        />
        <StatTile
          value={totals.pendingKyc}
          label="Landlords awaiting verification"
          tone={totals.pendingKyc > 0 ? 'warn' : undefined}
        />
      </div>

      {/* Three separate single-series charts rather than one chart with three
          y-scales: view counts dwarf signups, and forcing them onto one axis would
          invent a relationship that is not there. */}
      <div className="chart-grid">
        <TrendChart
          title="Listing views per day"
          subtitle="Last 30 days"
          data={data.viewsByDay}
          accessibleLabel="Listing views per day over the last 30 days"
        />
        <TrendChart
          title="New accounts per day"
          subtitle="Last 30 days"
          data={data.signupsByDay}
          accessibleLabel="New user accounts per day over the last 30 days"
        />
        <TrendChart
          title="Applications per day"
          subtitle="Last 30 days"
          data={data.applicationsByDay}
          accessibleLabel="Rental applications per day over the last 30 days"
        />
      </div>

      <div className="chart-grid">
        <BarList
          title="Towns with the most listings"
          subtitle="Approved listings, with the average advertised rent"
          keyHeader="Town"
          rows={data.topCities.map((row) => ({
            key: row.city,
            label: row.city,
            value: row.listings,
            note: `average ${formatXaf(row.avgPrice)}`
          }))}
          accessibleLabel="Number of approved listings by town"
        />

        <BarList
          title="Applications by stage"
          subtitle="All applications ever submitted"
          keyHeader="Stage"
          rows={statusRows(data.applicationsByStatus, [
            'submitted',
            'under_review',
            'approved',
            'rejected',
            'withdrawn'
          ])}
          accessibleLabel="Applications grouped by stage"
        />
      </div>

      <div className="panel-grid">
        <section className="panel">
          <h2>Listings by availability</h2>
          <ul className="kv-list">
            {statusRows(data.listingsByStatus, ['available', 'pending', 'rented', 'draft']).map((row) => (
              <li key={row.key}>
                <StatusBadge status={row.key} />
                <span>{row.value}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="panel">
          <h2>Listings by moderation state</h2>
          <ul className="kv-list">
            {statusRows(data.listingsByModeration, ['approved', 'pending', 'rejected']).map((row) => (
              <li key={row.key}>
                <StatusBadge status={row.key} />
                <span>{row.value}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/listings" className="link-strong">
            Open the moderation queue
          </Link>
        </section>

        <section className="panel">
          <h2>Users by role</h2>
          <ul className="kv-list">
            {Object.entries(data.usersByRole).map(([role, count]) => (
              <li key={role}>
                <span>{titleCase(role)}</span>
                <span>{count}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/users" className="link-strong">
            Manage users
          </Link>
        </section>
      </div>
    </div>
  );
};

export default AdminOverview;
